import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
} from "@mui/material"
import dayjs from "dayjs"
import { FC, useMemo, useState } from "react"
import { ClientFragment } from "../../generated/graphql"
import { Order } from "../../utils/types"

const EmotionalCooldownRatings: FC<{ client: ClientFragment }> = ({
  client,
}) => {
  const [dateOrder, setDateOrder] = useState<Order>("desc")
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)

  const relatedRatings = useMemo(() => {
    const ratingsByTransactionId = new Map()

    for (let rating of client.activity_rating) {
      if (ratingsByTransactionId.has(rating.transaction_id)) {
        const relatedEntry = {
          ...ratingsByTransactionId.get(rating.transaction_id),
          post_rating: rating.value,
          post_activity: rating.activity_id,
          post_activity_name: rating.activity_selected.display_name,
        }
        ratingsByTransactionId.set(rating.transaction_id, relatedEntry)
      } else {
        const entry = {
          id: rating.transaction_id,
          raw_created_at: rating.client_created_at,
          created_at: dayjs(rating.client_created_at).format(
            "MMMM D, YYYY HH:mm"
          ),
          pre_rating: rating.value,
          activity: rating.activity_id,
          pre_activity_name: rating.activity_selected.display_name,
        }
        ratingsByTransactionId.set(rating.transaction_id, entry)
      }
    }

    return Array.from(ratingsByTransactionId.values()).sort((left, right) => {
      const leftDate = dayjs(left.raw_created_at).valueOf()
      const rightDate = dayjs(right.raw_created_at).valueOf()

      return dateOrder === "desc" ? rightDate - leftDate : leftDate - rightDate
    })
  }, [client.activity_rating, dateOrder])

  return (
    <Box>
      <TableContainer
        component={Paper}
        elevation={0}
        sx={(theme) => ({
          overflowX: "unset",
          mb: 2,
          backgroundColor: theme.palette.background.paper,
          border: `1px solid ${
            theme.palette.vira?.border ?? theme.palette.divider
          }`,
          borderRadius: 1,
          boxShadow: "none",
        })}
      >
        <Table
          aria-label="Distress ratings"
          sx={(theme) => ({
            color: theme.palette.vira?.ink ?? theme.palette.text.primary,
            backgroundColor: "transparent",
            "& .MuiTableCell-root": {
              fontWeight: 400,
              fontSize: 14,
              fontStyle: "normal",
              color: theme.palette.vira?.ink ?? theme.palette.text.primary,
              borderColor: theme.palette.vira?.border ?? theme.palette.divider,
            },
            "& .MuiTableSortLabel-root": {
              color: theme.palette.vira?.ink ?? theme.palette.text.primary,
              "&:hover": {
                color: theme.palette.vira?.ink ?? theme.palette.text.primary,
              },
              "&.Mui-active": {
                color: theme.palette.vira?.ink ?? theme.palette.text.primary,
              },
              "& .MuiTableSortLabel-icon": {
                color: `${
                  theme.palette.vira?.muted ?? theme.palette.text.secondary
                } !important`,
              },
            },
          })}
        >
          <TableHead>
            <TableRow>
              <TableCell align="left" sortDirection={dateOrder}>
                <TableSortLabel
                  active={true}
                  direction={dateOrder}
                  onClick={() =>
                    setDateOrder((currentOrder) =>
                      currentOrder === "desc" ? "asc" : "desc"
                    )
                  }
                >
                  Date
                </TableSortLabel>
              </TableCell>
              <TableCell align="center">Activity</TableCell>
              <TableCell align="center">Pre Activity Rating</TableCell>
              <TableCell align="center">Post Activity Rating</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {relatedRatings
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((rating) => (
                <TableRow key={rating.id}>
                  <TableCell component="th" scope="row" align="left">
                    {rating.created_at}
                  </TableCell>
                  <TableCell align="center">
                    {rating.post_activity_name}
                  </TableCell>
                  <TableCell align="center">{rating.pre_rating}</TableCell>
                  <TableCell align="center">{rating.post_rating}</TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={relatedRatings.length}
        page={page}
        rowsPerPage={rowsPerPage}
        rowsPerPageOptions={[5, 10, 25]}
        SelectProps={{
          inputProps: {
            "aria-label": "rows per page",
          },
          native: true,
        }}
        onPageChange={(_e, newPage) => setPage(newPage)}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10))
          setPage(0)
        }}
        sx={(theme) => ({
          color: theme.palette.vira?.ink ?? theme.palette.text.primary,
          "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows, & .MuiTablePagination-select, & .MuiIconButton-root":
            {
              color: theme.palette.vira?.ink ?? theme.palette.text.primary,
            },
          "& .MuiIconButton-root.Mui-disabled": {
            color: theme.palette.vira?.disabledText,
          },
        })}
      />
    </Box>
  )
}

export default EmotionalCooldownRatings
